<template>
  <div class="pc__wrapper">
    <!-- Image section -->
    <a
      :href="`/products/${product.handle}?color=${selectedColor}`"
      :class="['pc__images', { OOS: showOOSOverlay }]"
      :data-stio-gtm-click-id="gtmTrackId != '' ? gtmTrackId : null"
      :data-stio-product-name="product.title"
      :data-stio-product-handle="product.handle"
      :data-stio-collection-name="collectionTitle || null"
      :data-stio-collection-handle="collectionHandle || null"
      :data-stio-ab-image-variant="abImageVariant"
      tabindex="0"
    >
      <div v-if="showOOSOverlay" class="pc__oos-badge">SOLD OUT</div>
      <ProductImage
        :enableImageCarousel="enableImageCarousel"
        :selectedColor="selectedColor"
        :product="product"
        :isLoadingLazy="isLoadingLazy"
        @ab-image-variant-change="onAbImageVariantChange"
      />
      <WishlistCTA
        v-if="showWishlistIcon"
        :product="product"
        :selectedVariantDetails="{ Color: selectedColor }"
        :firstOptionName="firstKeyName"
        :showOptions="true"
        customClass="product-card_wishlisticon"
      />
    </a>

    <!-- Info section -->
    <div :class="['pc__info', { 'pc__info--secondTurn': isSecondTurn }]">
      <div class="pc__info-inner">
        <Swatch
          v-if="!isSecondTurn && selectedColor"
          :colorOptions="renderColorOptions"
          :selectedColor="selectedColor"
          :isLoadingLazy="isLoadingLazy"
          @color-change="updateSelectedColor"
        />
        <div :class="['pc__product-title', { 'pc__product-title--margin': !selectedColor }]" role="heading" aria-level="2">{{ product.title }}</div>
        <div v-if="enableReviews" :class="['pc__reviews', { loading: loadMoreProducts }]" tabindex="-1">
          <div
            v-if="!isSecondTurn"
            class="yotpo-widget-instance"
            data-yotpo-instance-id="498880"
            :data-yotpo-product-id="product.id"
          ></div>
        </div>
        <div class="pc__price-wrapper">
          <div :class="['pc__price', { bold: comparePrice > price }]">
            <span v-if="isSecondTurn">Used</span> {{ formatPrice(price) }}
          </div>
          <div v-if="isPriceRange && customerDiscountVal <= 0" class="pc__price-range">{{ isPriceRange }}</div>
          <div v-else-if="comparePrice > price" class="pc__compare-price">
            {{ formatPrice(comparePrice) }}
          </div>
        </div>
      </div>
      <CompareButton
        v-if="showCompare && product.compare_data.length"
        :productId="product.id"
        :compareProducts="compareProducts"
        :maxCompareItems="windowWidth > 1023 ? 4 : 3"
        :windowWidth="windowWidth"
        @compare-change="onCompareClick"
      />
      <div v-if="quickviewBtn" class="pc__qv-btn" @click.prevent="toggleQuickView" tabindex="0">Add to Cart</div>
    </div>
  </div>
</template>

<script setup>
  /** ProductCardTypeB is used in collaboration pages like Hest, Guitar, Wilderdog */
  import { computed, onMounted, toRefs, ref, onBeforeMount, reactive, watch } from 'vue';
  import ProductImage from './product-image.vue';
  import Swatch from './product-swatch.vue';
  import CompareButton from './compare-button.vue';
  import useWindowResize from '../../../utils/use-window-resize';
  import WishlistCTA from '../wishlist-cta.vue';
  import productCardProps from './product-card-props';

  const props = defineProps({
    product: productCardProps.product,
    quickviewBtn: productCardProps.quickviewBtn,
    showOOSbadge: productCardProps.showOOSbadge,
    enableImageCarousel: productCardProps.enableImageCarousel,
    isLoadingLazy: productCardProps.isLoadingLazy,
    showWishlistIcon: productCardProps.showWishlistIcon,
    isSecondTurn: productCardProps.isSecondTurn,
    enableReviews: productCardProps.enableReviews,
    loadMoreProducts: productCardProps.loadMoreProducts,
    showCompare: productCardProps.showCompare,
    compareProducts: productCardProps.compareProducts,
    gtmTrackId: productCardProps.gtmTrackId,
    collectionHandle: productCardProps.collectionHandle,
    collectionTitle: productCardProps.collectionTitle,
  });

  const {
    product,
    quickviewBtn,
    showOOSbadge,
    isLoadingLazy,
    showWishlistIcon,
    isSecondTurn,
    enableReviews,
    loadMoreProducts,
    showCompare,
    compareProducts,
    collectionHandle,
    collectionTitle,
  } = toRefs(props);

  const firstKeyName = reactive(product.value.options[0].name);
  const selectedColor = ref(null);
  const customerDiscountVal = ref(0);
  const abImageVariant = ref(null);
  const { windowWidth } = useWindowResize();
  const emit = defineEmits(['on-compare-click']);

  const onAbImageVariantChange = (variant) => {
    abImageVariant.value = variant;
  };

  onBeforeMount(() => {
    getCustomerDiscount();
  });

  onMounted(() => {
    initialSetup();
  });

  watch(product, () => {
    initialSetup();
  });

  const hasBISTag = computed(() => {
    return product.value.tags.some((tag) => tag.toLowerCase().startsWith('bis:'));
  });

  const showOOSOverlay = computed(() => {
    return !product.value.available && showOOSbadge.value && !hasBISTag.value;
  });

  const activeColorData = computed(() => {
    return selectedColor.value && renderColorOptions.value[selectedColor.value];
  });

  const price = computed(() => {
    if (activeColorData.value && activeColorData.value.comparePrice > 0) {
      const { comparePrice, price } = activeColorData.value;

      if (comparePrice * (1 - customerDiscountVal.value) < price) {
        return comparePrice * (1 - customerDiscountVal.value);
      } else {
        return price;
      }
    } else {
      return (product.value.price / 100) * (1 - customerDiscountVal.value);
    }
  });

  const comparePrice = computed(() => {
    if (activeColorData.value) {
      if (activeColorData.value.comparePrice > 0) {
        return activeColorData.value.comparePrice;
      } else {
        return activeColorData.value.price;
      }
    } else {
      return product.value.compare_at_price / 100;
    }
  });

  const isPriceRange = computed(() => {
    const { min_price, max_price } = product.value;

    if (min_price != max_price) {
      return `(${formatPrice(min_price / 100)} - ${formatPrice(max_price / 100)})`;
    } else {
      return false;
    }
  });

  const renderColorOptions = computed(() => {
    const { variants, tags, images, available } = product.value;

    const firstLevelKeys = variants.reduce((set, variant) => {
      const property = variant.option1;
      const setItem = (set[property] = set[property] || { items: [] });
      setItem.items.push(variant);
      if (variant.available) setItem.items.available = true;
      return set;
    }, {});

    Object.entries(firstLevelKeys).forEach(([key, value]) => {
      const isAvailable = value.items.available;
      const bisData = tags.filter((tag) => tag.toLowerCase().includes(`bis: ${key.toLowerCase()}`));

      if (isAvailable || bisData.length || !available) {
        const imagePattern = new RegExp(`^\\s*${key.toLowerCase()}\\s*swatch\\s*$`);
        value.image = images.filter((image) => {
          const alt = image.alt.toLowerCase();
          return alt && imagePattern.test(alt);
        })[0];

        value.price = Number(value.items[0].price / 100);
        value.comparePrice = Number(value.items[0].compare_at_price / 100);
      } else {
        delete firstLevelKeys[key];
      }
    });
    return firstLevelKeys;
  });

  const updateSelectedColor = (color) => {
    selectedColor.value = color;
  };

  const initialSetup = () => {
    if (renderColorOptions.value && firstKeyName === 'Color') {
      selectedColor.value = Object.keys(renderColorOptions.value)[0];
    }
  };

  const getCustomerDiscount = () => {
    customerDiscountVal.value = window.customerDiscount(product.value.tags);
  };

  const toggleQuickView = () => {
    window.toggleDrawer(true, 'QuickView', { product: product.value, selectedColor: selectedColor.value });
  };

  const formatPrice = (value) => {
    return window.formattedPrice(value);
  };

  const onCompareClick = (checked) => {
    product.value.Color = selectedColor.value;
    emit('on-compare-click', checked, product.value);
  };
</script>
